import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

export default function DeviationDocumentation() {
  return (
    <>
      <SEO
        title="Deviation Documentation Workflows for Regulated Labs | LabComplyx"
        description="Create faster, clearer deviation records with full traceability from event to corrective action. LabComplyx governed workflows for CLIA, ISO 17025, and GxP labs."
        canonical="https://labcomplyx.com/solutions/deviation-documentation"
        breadcrumbs={[
          { name: "Home", url: "https://labcomplyx.com/" },
          { name: "Solutions", url: "https://labcomplyx.com/solutions" },
          { name: "Deviation Documentation", url: "https://labcomplyx.com/solutions/deviation-documentation" },
        ]}
      />
      <div className="pt-24">
        <section className="pb-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 max-w-2xl">
            <span className="inline-block text-xs font-semibold text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">Solution</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-5">Deviation documentation built for regulated labs</h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              LabComplyx helps labs create deviation records faster, with traceable logic from event to corrective action — every record human-reviewed and audit-ready before it enters your quality system.
            </p>
            <Link to="/ValidationIntake"><Button size="lg" className="gap-2">Start with a Free Gap Audit <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>
        </section>

        <section className="py-16 bg-muted/30 border-y border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-5">Why deviation records fail audits</h2>
                <ul className="space-y-2.5">
                  {["Incomplete event description lacking specificity", "No documented immediate containment actions", "Root cause not supported by evidence", "Corrective actions not linked back to root cause", "Missing follow-up and effectiveness verification"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground"><span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0 mt-1.5" />{b}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-card border border-border rounded-xl p-7">
                <h3 className="font-semibold text-foreground mb-4">LabComplyx deviation workflow</h3>
                <ul className="space-y-2.5">
                  {["Structured deviation intake with event classification", "Governed AI drafting with deviation-specific guardrails", "Traceable linkage: event → root cause → action → closure", "Human review checkpoint before record finalization", "Full audit trail with tamper-evident entries"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground"><Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />{b}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}